import React from 'react';
import { Box, Paper, Tooltip, Typography } from '@mui/material';

import { useTenantVersions } from '../../api/useTenantVersions';
import { colors, typo } from '../../theme/tokens';
import { timeAgo } from '../../utils/timeAgo';
import EnvPill from './EnvPill';
import StatusChip from './StatusChip';
import VersionChip from './VersionChip';
import VersionDeltaChip from './VersionDeltaChip';

/**
 * Tenant version board. One row per readable env showing THIS tenant's own
 * version on that env. Versions are PER-TENANT (mig 219): each tenant has its
 * own 1.0.x sequence, so there is no env-wide "head" to show — a tenant that has
 * never published reads 1.0.0 (its genesis baseline) on its home env, and "not
 * promoted" on envs it hasn't reached.
 *
 * Layout: an aligned 3-column grid (env pill | version + delta | age + home
 * marker). The HOME env row is lifted with a brand tint + left accent so "where
 * is this tenant edited" is unmissable.
 */
const EnvVersionStrip: React.FC<{ tenantCode: string }> = ({ tenantCode }) => {
  const { rows: tenantRows } = useTenantVersions(tenantCode || undefined);
  if (!tenantCode || tenantRows.length === 0) return null;

  return (
    <Paper
      elevation={0}
      sx={{ border: `1px solid ${colors.borderLight}`, borderRadius: '12px', p: 2, mb: 2 }}
      data-testid="env-version-strip"
    >
      <Typography sx={{ ...typo.sectionLabel, mb: 1.5 }}>Tenant version · by environment</Typography>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
        {tenantRows.map((row) => {
          return (
            <Box
              key={row.env}
              data-testid={`env-version-row-${row.env}`}
              sx={{
                display: 'grid',
                gridTemplateColumns: '132px 1fr auto',
                alignItems: 'center',
                gap: 1.5,
                px: 1.25,
                py: 1,
                borderRadius: '10px',
                ...(row.isHome
                  ? {
                      background: `linear-gradient(90deg, ${colors.brandLight}, ${colors.bgWhite} 65%)`,
                      boxShadow: `inset 3px 0 0 ${colors.brand}`,
                    }
                  : { backgroundColor: colors.bgWhite, border: `1px solid ${colors.borderLight}` }),
              }}
            >
              {/* col 1 — env identity */}
              <EnvPill env={row.env} size="md" />

              {/* col 2 — the tenant's version state on this env */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                {row.isError ? (
                  <Typography sx={{ ...typo.tinyText }}>not reachable</Typography>
                ) : row.isLoading ? (
                  <Typography sx={{ ...typo.tinyText }}>…</Typography>
                ) : !row.promoted || !row.versionStr ? (
                  row.isHome ? (
                    // Home env, never published: show the 1.0.0 genesis baseline
                    // (per-tenant). Publishing this tenant mints its 1.0.1.
                    <Tooltip title="This tenant hasn't published yet — 1.0.0 is its baseline. Publishing mints 1.0.1.">
                      <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 1 }}>
                        <VersionChip version="1.0.0" variant="home" />
                        <Typography sx={{ ...typo.tinyText, color: colors.textTertiary }}>unpublished</Typography>
                      </Box>
                    </Tooltip>
                  ) : (
                    <Tooltip title="This tenant has not been promoted to this environment yet.">
                      <Box component="span">
                        <StatusChip status="pending" label="not promoted" />
                      </Box>
                    </Tooltip>
                  )
                ) : (
                  <>
                    <VersionChip version={row.versionStr} variant={row.isHome ? 'home' : 'neutral'} />
                    <VersionDeltaChip behind={row.behind} isLatest={row.isLatest} />
                    {row.isPartial && (
                      <StatusChip
                        status="partial"
                        label="partial"
                        tooltipContent="The last apply to this env completed with some sections failed."
                      />
                    )}
                  </>
                )}
              </Box>

              {/* col 3 — muted context (env head + age) + home marker */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, justifyContent: 'flex-end' }}>
                {!row.isError && !row.isLoading && row.promoted && row.advancedAt && (
                  <Typography sx={{ ...typo.tinyText }} title={new Date(row.advancedAt).toLocaleString()}>
                    {timeAgo(row.advancedAt)}
                  </Typography>
                )}
                {row.isHome && (
                  <Box
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 0.6,
                      px: 1,
                      py: 0.3,
                      borderRadius: '999px',
                      backgroundColor: colors.bgWhite,
                      border: `1px solid ${colors.warningBorder}`,
                      fontSize: 10,
                      fontWeight: 800,
                      letterSpacing: '0.4px',
                      textTransform: 'uppercase',
                      fontFamily: 'Manrope, sans-serif',
                      color: colors.warningText,
                    }}
                  >
                    <Box
                      sx={{
                        width: 7,
                        height: 7,
                        borderRadius: '50%',
                        backgroundColor: colors.successIcon,
                        boxShadow: `0 0 0 3px ${colors.successBg}`,
                      }}
                    />
                    Home · editable
                  </Box>
                )}
              </Box>
            </Box>
          );
        })}
      </Box>
    </Paper>
  );
};

export default EnvVersionStrip;
